import api from 'api';
import {useCallback, useMemo, useState} from 'react';
import {useInfiniteQuery, useMutation, useQueryClient} from 'react-query';
import {ListResponse} from 'types';
import {CompanyNotificationLog} from 'types/notification';
import {NotificationType} from 'types/notification-settings';

const STORAGE_KEY = 'notification-filter-types';
const PAGE_SIZE = 20;

const readStoredTypes = (): NotificationType[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

const fetchNotifications = async (
  notificationTypes: NotificationType[],
  offset: number
) => {
  const params = Object.fromEntries(
    Object.entries({
      limit: PAGE_SIZE,
      offset,
      notification_types: notificationTypes.join(','),
    }).filter(([_, value]) => value !== undefined && value !== '')
  );

  const {data} = await api.get<ListResponse<CompanyNotificationLog>>(
    '/companies/company-notification-logs/',
    {params}
  );
  return data;
};

const markAsRead = async ({id}: {id: number}) => {
  const {data} = await api.post(
    `/companies/company-notification-logs/${id}/mark-read/`
  );
  return data;
};

const markAllAsRead = async () => {
  const {data} = await api.post(
    '/companies/company-notification-logs/mark-all-read/'
  );
  return data;
};

export const useNotification = () => {
  const queryClient = useQueryClient();
  const [notificationTypes, setNotificationTypesState] = useState<
    NotificationType[]
  >(readStoredTypes);

  const setNotificationTypes = useCallback((types: NotificationType[]) => {
    setNotificationTypesState(types);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(types));
  }, []);

  const {data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage} =
    useInfiniteQuery(
      ['company-notifications', notificationTypes],
      ({pageParam = 0}) => fetchNotifications(notificationTypes, pageParam),
      {
        getNextPageParam: (lastPage) => {
          const nextOffset = lastPage.offset + lastPage.limit;
          return nextOffset < lastPage.count ? nextOffset : undefined;
        },
      }
    );

  const notifications = useMemo(
    () => (data ? data.pages.flatMap((page) => page.results) : []),
    [data]
  );

  const unreadCount = data?.pages[0]?.unread_count ?? 0;

  const {mutateAsync: markAsReadMutation} = useMutation({
    mutationFn: markAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries('company-notifications');
    },
  });

  const {mutateAsync: markAllAsReadMutation} = useMutation({
    mutationFn: markAllAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries('company-notifications');
    },
  });

  return {
    notifications,
    isLoading,
    unreadCount,
    notificationTypes,
    setNotificationTypes,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    onMarkAsRead: markAsReadMutation,
    onMarkAllAsRead: markAllAsReadMutation,
  };
};
